import { useCallback, useEffect, useState } from 'react';
import { readHistory, readRules } from '../api';
import type { RuleVersion, RulesReply } from '../api-types';

export type RulesState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly rules: RulesReply;
      readonly history: readonly RuleVersion[];
    }
  | { readonly status: 'error' };

/** The rules in force and their history; the history failing alone never hides the rules. */
export function useRules() {
  const [state, setState] = useState<RulesState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void Promise.all([readRules(), readHistory()]).then(([rules, history]) => {
      if (!live) return;
      if (!rules.ok) {
        setState({ status: 'error' });
        return;
      }
      setState({
        status: 'ready',
        rules: rules.value,
        history: history.ok ? history.value.versions : [],
      });
    });
    return () => {
      live = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}
